import axios from 'axios'
import { Auth } from 'aws-amplify'
import { newRequestKey } from './billing'
import { uploadVideo } from './videoUploads'

const uploads = new WeakMap()
export function resourcesChanged() {
  window.dispatchEvent(new Event('billing:changed'))
  try { localStorage.setItem('billing-updated', String(Date.now())) } catch (_) { /* Optional cross-tab refresh. */ }
}
export function showResourceError(error) {
  const data = (error.response && error.response.data) || error
  const code = data.error || data.code
  const status = error.response && error.response.status
  let message = typeof data === 'object' && data.message
  if (status === 413) message = 'This file exceeds the upload gateway limit. Choose a smaller file.'
  else if (error.code === 'ECONNABORTED') message = 'The upload took too long. Your changes are still here; please try again.'
  window.dispatchEvent(new CustomEvent('resources:notice', {
    detail: {
      kind: ({ STORAGE_LIMIT_EXCEEDED: 'storage', INSUFFICIENT_CREDITS: 'ai' })[code] || 'error',
      message: message || 'The operation could not be completed. Please try again.',
    },
  }))
}
export async function uploadMedia(data, source, config = {}) {
  let state = uploads.get(source)
  if (!state) { state = { key: newRequestKey() }; uploads.set(source, state) }
  if (state.result) return state.result
  if (state.pending) return state.pending
  state.pending = (async () => {
    try {
      const { onProcessing, ...requestConfig } = config
      const authorization = async () => (await Auth.currentSession()).getAccessToken().getJwtToken()
      let payload = data
      const binary = typeof Blob !== 'undefined' && data.buffer instanceof Blob
      if (binary && /^video\//.test(data.mime)) {
        const response = await uploadVideo(data.buffer, data.originalname, state, config)
        state.result = response
        if (onProcessing) onProcessing(null)
        resourcesChanged()

        return response
      }
      if (binary) {
        payload = new FormData()
        payload.append('file', data.buffer, data.originalname || source.name || 'media')
        payload.append('originalname', data.originalname || source.name || 'media')
        payload.append('mime', data.mime)
      }
      let response
      if (state.job) {
        response = await axios.get(`https://content.selfbiography.com/api/upload-status/${encodeURIComponent(state.job)}`, {
          timeout: 30000, headers: { Authorization: await authorization() },
        })
      } else {
        response = await axios.post('https://content.selfbiography.com/api/upload', payload, {
          ...requestConfig,
          timeout: binary ? 600000 : 180000,
          headers: { Authorization: await authorization(), 'Idempotency-Key': state.key, 'X-Media-Processing': 'async' },
        })
      }
      const deadline = Date.now() + 30 * 60 * 1000
      while (response.data && response.data.Processing) {
        state.job = response.data.JobId
        if (onProcessing) onProcessing(response.data.Phase)
        if (Date.now() > deadline) throw new Error('Your video is still processing. Please retry to check its progress.')

        // Poll briefly authenticated requests rather than holding a gateway connection during conversion.
        // eslint-disable-next-line no-await-in-loop
        await new Promise(resolve => setTimeout(resolve, 2000))
        // eslint-disable-next-line no-await-in-loop
        response = await axios.get(`https://content.selfbiography.com/api/upload-status/${encodeURIComponent(state.job)}`, {
          // eslint-disable-next-line no-await-in-loop
          timeout: 30000, headers: { Authorization: await authorization() },
        })
      }
      if (!response.data || response.data.error || !response.data.Data || !response.data.Data.Key) throw new Error('Upload was not confirmed. Please try again.')
      state.result = response
      state.job = null
      if (onProcessing) onProcessing(null)
      resourcesChanged()

      return response
    } catch (error) {
      const code = error.response && error.response.data && error.response.data.error
      if (code) state.job = null
      if (config.onProcessing) config.onProcessing(null)
      if (['UPLOAD_FINISHED', 'UPLOAD_KEY_REUSED'].includes(code)) uploads.delete(source)
      showResourceError(error)
      throw error
    } finally { state.pending = null }
  })()

  return state.pending
}
